import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { CalendarDays } from "lucide-react";

export default function Footer() {
  const { t } = useI18n();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/events");
        if (!cancelled) setEvents(data || []);
      } catch (_) {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <footer data-testid="site-footer" className="mt-24 border-t border-[#C1B49A] bg-[#EAE0CA]/60">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          <div className="md:col-span-2">
            <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2">
              <CalendarDays size={12} /> {t("footer.events_eyebrow")}
            </p>
            <h3 className="font-display text-3xl md:text-4xl tracking-tight text-[#1A1918] mb-5">
              {t("footer.events_heading")}
            </h3>
            {events.length === 0 ? (
              <p className="text-sm italic text-[#7A7061]" data-testid="footer-events-empty">
                {t("footer.events_empty")}
              </p>
            ) : (
              <ul className="space-y-3" data-testid="footer-events-list">
                {events.map((e) => (
                  <li
                    key={e.id}
                    data-testid={`footer-event-${e.id}`}
                    className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 border-l-2 border-[#8B3A3A]/60 pl-4 py-1"
                  >
                    <span className="font-display text-xl text-[#1A1918] leading-tight">{e.name}</span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#8B3A3A] font-mono font-semibold mt-1 sm:mt-0">
                      {e.date_label}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-col items-start md:items-end justify-end gap-2 text-sm">
            <span className="seal text-2xl" aria-hidden>✦</span>
            <span className="font-display italic text-xl text-[#1A1918]">NLP Magick</span>
            <span className="text-[10px] tracking-[0.28em] uppercase font-mono text-[#8B3A3A]">
              {t("landing.footer_motto")}
            </span>
            <span className="text-[10px] tracking-[0.24em] uppercase font-mono text-[#7A7061]">
              {t("landing.footer_signature")}
            </span>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 md:justify-end text-[11px] tracking-[0.18em] uppercase font-mono">
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-[#7A7061] hover:text-[#8B3A3A] underline underline-offset-4 decoration-[#C1B49A] hover:decoration-[#8B3A3A] transition-colors">
                {t("footer.privacy")}
              </Link>
              <Link to="/terms" data-testid="footer-terms-link" className="text-[#7A7061] hover:text-[#8B3A3A] underline underline-offset-4 decoration-[#C1B49A] hover:decoration-[#8B3A3A] transition-colors">
                {t("footer.terms")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
